/**
 * Mock payload for EnterpriseReport / PrintEnterpriseReportPage preview mode.
 * Shape mirrors build_enterprise_report_payload() from backend (§5.3).
 */
const _abilities = (vals) => [
  { code: 'cognitive_understanding', name: '认知理解力', avg: vals[0] },
  { code: 'learning_adaptation', name: '学习适应力', avg: vals[1] },
  { code: 'scenario_judgment', name: '场景判断力', avg: vals[2] },
  { code: 'instruction_interaction', name: '指令交互力', avg: vals[3] },
  { code: 'task_orchestration', name: '任务编排力', avg: vals[4] },
  { code: 'result_evaluation', name: '结果评估力', avg: vals[5] },
  { code: 'safety_responsibility', name: '安全责任力', avg: vals[6] },
  { code: 'innovation_transformation', name: '创新转化力', avg: vals[7] },
];

export const mockEnterpriseReportPayload = {
  scope: 'enterprise',
  cover: {
    enterprise_name: '前哨科技有限公司',
    class_count: 3,
    headcount: 60,
  },
  overview: {
    enterprise: { id: 1, name: '前哨科技有限公司', industry: '互联网 / SaaS' },
    class_count: 3,
    total_unique_headcount: 60,
    completed_headcount: 52,
    classes_kpi_matrix: [
      {
        class_id: 1, name: '2026春季技术骨干特训', size: 18, completion_rate: 100.0, team_avg: 75.8,
        l_dist: [{ l_grade: 'L1', count: 0, pct: 0 }, { l_grade: 'L2', count: 4, pct: 22.2 }, { l_grade: 'L3', count: 8, pct: 44.4 }, { l_grade: 'L4', count: 6, pct: 33.3 }],
        short_dims: [{ dim_code: 'innovation_transformation', dim_name: '创新转化力', avg_score: 68 }],
        risk_count: 2,
        water_fill_rate: 5.6, flagged_count: 1, completed_with_quality: 18,
      },
      {
        class_id: 2, name: '2026春季管培生集训', size: 22, completion_rate: 95.5, team_avg: 71.2,
        l_dist: [{ l_grade: 'L1', count: 1, pct: 4.5 }, { l_grade: 'L2', count: 7, pct: 31.8 }, { l_grade: 'L3', count: 10, pct: 45.5 }, { l_grade: 'L4', count: 3, pct: 13.6 }],
        short_dims: [{ dim_code: 'task_orchestration', dim_name: '任务编排力', avg_score: 58 }, { dim_code: 'innovation_transformation', dim_name: '创新转化力', avg_score: 55 }],
        risk_count: 3,
        water_fill_rate: 13.6, flagged_count: 3, completed_with_quality: 21,
      },
      {
        class_id: 3, name: '2026春季销售业务骨干班', size: 31, completion_rate: 83.9, team_avg: 64.1,
        l_dist: [{ l_grade: 'L1', count: 4, pct: 12.9 }, { l_grade: 'L2', count: 17, pct: 54.8 }, { l_grade: 'L3', count: 10, pct: 32.3 }, { l_grade: 'L4', count: 0, pct: 0 }],
        short_dims: [{ dim_code: 'instruction_interaction', dim_name: '指令交互力', avg_score: 52 }, { dim_code: 'task_orchestration', dim_name: '任务编排力', avg_score: 48 }],
        risk_count: 11,
        water_fill_rate: 35.5, flagged_count: 11, completed_with_quality: 31,
      },
    ],
    cross_class_ability_heatmap: [
      { class_id: 1, name: '2026春季技术骨干特训', abilities: _abilities([87, 85, 78, 74, 70, 76, 88, 68]) },
      { class_id: 2, name: '2026春季管培生集训', abilities: _abilities([81, 78, 72, 66, 58, 68, 84, 55]) },
      { class_id: 3, name: '2026春季销售业务骨干班', abilities: _abilities([72, 68, 66, 52, 48, 58, 75, 46]) },
    ],
    classes_overflow: null,
    company_layer_distribution: [
      { l_grade: 'L1', count: 5, pct: 9.6 }, { l_grade: 'L2', count: 28, pct: 53.8 },
      { l_grade: 'L3', count: 13, pct: 25.0 }, { l_grade: 'L4', count: 6, pct: 11.5 },
    ],
    company_ability_radar: _abilities([78, 74, 68, 61, 55, 66, 81, 54]),
    company_trait_distribution: [
      { trait_code: 'ai_willingness', trait_name: 'AI 协作意愿', avg: 3.1 },
      { trait_code: 'independent_thinking', trait_name: '独立思考', avg: 2.8 },
      { trait_code: 'proactive_learning', trait_name: '主动学习', avg: 3.0 },
    ],
    company_position_top5: [
      { position: '工程师', count: 18 }, { position: '销售', count: 15 },
      { position: '产品经理', count: 12 }, { position: '管培生', count: 9 }, { position: 'HR', count: 6 },
    ],
  },
  scheduling: {
    generated_at: '2026-06-01T08:00:00+00:00',
    ai_meta: { model: 'glm-4.6', prompt_version: 5 },
    headline_tag: '整体 L2 偏上，创新转化 + 任务编排是跨班共性洼地',
    comprehensive_interpretation:
      '企业共 3 个在训班级、60 名学员参与测评，全员均分约 69。跨班级看，创新转化力与任务编排力是组织级共性短板，技术骨干班整体领先而销售骨干班偏弱，呈明显梯度。建议对共性洼地合班统筹，对梯度明显的维度分层推进，高带低混班实战。',
    scheduling_recommendations: [
      {
        title: '「创新转化力」合班统一公开课 + 跨班级最佳实践复盘',
        target_classes: [1, 2, 3], covered_dims: ['innovation_transformation'], priority: 'P0', merge_strategy: '合班',
        rationale_for_strategy: '3 个班级都把创新转化力列入主要短板，是典型组织级共性短板，方法论跨职能相通，适合合班单次解决。',
        improvement_directions: ['推动会用 AI 的人主动产出方法模板', '建立跨部门方法库共享机制', '识别已跑通但未规模化的 AI 应用'],
        resource_efficiency_note: '合班单次解决 3 班共性问题，相比每班单独安排节省约 70% 资源与时间成本，并促成横向最佳实践传播。',
        predicted_coverage: 60,
      },
      {
        title: '「任务编排力」分层小班 + 高带低混班实战',
        target_classes: [2, 3], covered_dims: ['task_orchestration'], priority: 'P0', merge_strategy: '分层',
        rationale_for_strategy: '两班在该维度呈明显梯度而非齐平短板，一刀切合班会出现快的吃不饱、慢的跟不上，建议分两批次推进。',
        improvement_directions: ['把复杂目标拆解为人做/AI做/协同做', '设计多步骤工作流并设审核点', '识别 AI 不可靠环节并准备兜底'],
        resource_efficiency_note: '分层后仍比各自办节省约 60% 资源，高带低形式让高 L 学员隐性经验显性化。',
        predicted_coverage: 53,
      },
    ],
    quarterly_schedule: [
      { month_label: '第 1 月', action: '创新转化力 合班公开复盘', coverage: 60 },
      { month_label: '第 1-2 月', action: '任务编排力 第一批 · L1-L2 学员', coverage: 30 },
      { month_label: '第 2 月', action: '任务编排力 第二批 · L3-L4 高带低', coverage: 23 },
    ],
    class_commentaries: [
      { class_id: 1, text: '2026春季技术骨干特训：18 人，团队均分 75.8，全员已完成，8 维度中 7 维度高于全公司均分，L4 学员占 1/3，是组织级最佳实践库的核心产出力量，建议安排做跨班高带低实战教练。' },
      { class_id: 2, text: '2026春季管培生集训：22 人，团队均分 71.2，整体稳健但任务编排与创新转化偏弱，建议参与两项合班/分层调度，重点补齐方法沉淀能力。' },
      { class_id: 3, text: '2026春季销售业务骨干班：31 人，团队均分 64.1，是全公司洼地班级，指令交互与任务编排短板明显，高+极高风险 11 人，建议优先分层补齐并做 1v1 跟进。' },
    ],
  },
  risks: {
    summary: {
      scope_kind: 'enterprise_class',
      total_members: 71,
      affected_members: 28,
      counts_by_register: { '极低': 5, '严重偏低': 8, '明显偏低': 9, '偏低': 6 },
      top_risk_dims: [
        { dim_code: 'task_orchestration', dim_name: '任务编排力', affected: 23, total: 71 },
      ],
      cohort_text_pending: false,
      cache_hit: false,
      generated_at: '2026-06-11T00:00:00Z',
    },
    dimensions: [
      {
        dim_code: 'task_orchestration', dim_name: '任务编排力', affected: 23, total: 71,
        mode: '偏低', band_counts: { '极低': 5, '偏低': 18 },
        enterprise_text: '在该维度偏弱的员工面对复杂目标时普遍缺乏分步拆解与多轮协作的意识，这种群体性短板会拉低整体交付质量与协同效率，其中少数员工的缺口尤为突出。建议组织层面建立常态化的任务编排训练与人工复核机制，系统性补齐这一能力。',
      },
    ],
  },
};
